import React, { useState, FormEvent } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL, getErrorMessage } from '../types';
import { useTranslation } from '../i18n';
import { useApiRequest } from '../useApiRequest';
import { FormLabel } from './FormLabel';
import { FormLegend } from './FormLegend';
import { FieldError } from './FieldError';
import { validateFormSubmit } from '../utils/formValidation';

export const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const { loading, call } = useApiRequest(async () => {
    await axios.post(`${API_URL}/auth/reset-password`, {
      token,
      newPassword: password,
    });
    setSuccess(true);
  });

  const handleSubmit = async (e: FormEvent) => {
    if (!validateFormSubmit(e)) return;
    e.preventDefault();
    setError('');
    setPasswordError('');

    if (password !== confirmPassword) {
      setPasswordError(t('reset.passwords_mismatch'));
      return;
    }

    if (password.length < 6) {
      setPasswordError(t('reset.password_too_short'));
      return;
    }

    try {
      await call();
    } catch (err) {
      setError(getErrorMessage(err, t('reset.generic_error')));
    }
  };

  if (!token) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h2>{t('reset.title')}</h2>
          <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
            {t('reset.invalid_link')}
          </div>
          <Link to="/login" className="btn btn-primary" style={{ width: '100%', textAlign: 'center' }}>
            {t('reset.back_to_login')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <h2>{t('reset.title')}</h2>
        {success ? (
          <>
            <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
              {t('reset.success')}
            </div>
            <Link to="/login" className="btn btn-primary" style={{ width: '100%', textAlign: 'center' }}>
              {t('reset.back_to_login')}
            </Link>
          </>
        ) : (
          <>
            <p className="login-subtitle">{t('reset.subtitle')}</p>
            {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}
            <form onSubmit={handleSubmit}>
              <FormLegend />
              <div className={`form-group${passwordError ? ' form-group--error' : ''}`}>
                <FormLabel required>{t('reset.new_password')}</FormLabel>
                <input
                  type="password"
                  value={password}
                  onChange={e => { setPassword(e.target.value); setPasswordError(''); }}
                  placeholder={t('reset.new_password_placeholder')}
                  required
                  disabled={loading}
                  aria-required="true"
                  aria-invalid={passwordError ? true : undefined}
                  aria-describedby={passwordError ? 'reset-password-error' : undefined}
                />
                <FieldError id="reset-password-error" message={passwordError} />
              </div>
              <div className={`form-group${passwordError ? ' form-group--error' : ''}`}>
                <FormLabel required>{t('reset.confirm_password')}</FormLabel>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => { setConfirmPassword(e.target.value); setPasswordError(''); }}
                  placeholder={t('reset.confirm_password_placeholder')}
                  required
                  disabled={loading}
                  aria-required="true"
                  aria-invalid={passwordError ? true : undefined}
                />
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
                {loading && <span className="btn-spinner" aria-hidden="true" />}
                {loading ? t('reset.resetting') : t('reset.submit')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
